import dayjs from "@/lib/helpers/dayjs";
import type { NoteHistory, NoteHistoryEvent } from "@/core/types";

import type { BatchCosignItem, NoteHistoryViewAs } from "../types";
import { NOTE_HISTORY_LABEL_MAP } from "../constants";

export function adaptBatchItem(items: BatchCosignItem[]) {
  return items.map((item) => ({
    document_id: item.documentId,
    document_type: item.documentType,
    patient_id: item.patientId,
  }));
}

export type NoteHistoryGroupEvent = NoteHistoryEvent & { statusLabel: string };

export type NoteHistoryGroup = {
  documentId: string;
  events: NoteHistoryGroupEvent[];
  groupLabel: string;
};

export function adaptNoteHistory(
  noteHistory: NoteHistory,
  viewAs: NoteHistoryViewAs,
): NoteHistoryGroup[] {
  const eventsByDocument = new Map<string, NoteHistoryGroupEvent[]>();

  for (const event of noteHistory) {
    const events = eventsByDocument.get(event.document_id) ?? [];

    events.push({
      ...event,
      statusLabel: NOTE_HISTORY_LABEL_MAP[event.cosignature_status][viewAs],
    });
    eventsByDocument.set(event.document_id, events);
  }

  const documents = Array.from(eventsByDocument, ([documentId, events]) => ({
    documentId,
    events: events.sort((a, b) => dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf()),
    isAddendum: events.some((event) => event.document_type === "note_addendum"),
  })).sort(
    (a, b) =>
      Number(a.isAddendum) - Number(b.isAddendum) ||
      dayjs(a.events[0].timestamp).valueOf() - dayjs(b.events[0].timestamp).valueOf(),
  );

  let addendumNumber = 0;

  return documents.map(({ documentId, events, isAddendum }) => ({
    documentId,
    events,
    groupLabel: isAddendum ? `Addendum ${++addendumNumber}` : "Note",
  }));
}
